import { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Table, Stack, Badge } from '@mantine/core';
import { TextInput, Button } from '@carbon/react';
import { CheckmarkFilled, CloseFilled } from '@carbon/icons-react';
import moment from 'moment';

import styles from './PointsHistoryTable.module.scss';

import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { useGetUserPointsQuery, useAcceptPointMutation, useRejectPointMutation } from 'api/pointsEndpoints';
import { PointDto } from 'api/dtos/point';
import { useTeamMemberContext } from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamInteractions/Interactions/Details/TeamMemberDetailsContext';
import WideSkeleton from 'Components/skeletons/WideSkeleton';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import PointOperationRemoval from './PointOperationRemoval/PointOperationRemoval';
import PointsOperationEdit from './PointsOperationEdit/PointsOperationEdit';
import AddOrRemovePoints from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamInteractions/Interactions/AddOrRemovePoints/AddOrRemovePoints';
import useCurrentEntityUsesPoints from 'hooks/useCurrentEntityUsesPoints';

type StatusFilter = 'all' | 'accepted' | 'pending' | 'rejected';

const statusFilterOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'accepted', label: 'Zaakceptowane' },
    { value: 'pending', label: 'Oczekujące' },
];

const getStatusBadge = (point: PointDto) => {
    if (point.isPendingApproval && !point.isAccepted) {
        return <Badge color="yellow" variant="light">Oczekuje</Badge>;
    }
    if (point.isAccepted) {
        return <Badge color="green" variant="light">Zaakceptowane</Badge>;
    }
    return <Badge color="red" variant="light">Odrzucone</Badge>;
};

const getSignedPointsValue = (value: number) => (value > 0 ? `+${value}` : value);

const PointsHistoryTableContent = () => {
    const intl = useIntl();
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const currentEntityId = useGetCurrentEntityId();
    const { values } = useTeamMemberContext();
    const userId = values.teamMemberId;

    const { data, isFetching, isError, refetch } = useGetUserPointsQuery({
        entityId: currentEntityId ?? '',
        teamMemberId: userId,
        includePending: true,
    }, { skip: !currentEntityId || !userId });

    const [acceptPoint, { isLoading: isAccepting }] = useAcceptPointMutation();
    const [rejectPoint, { isLoading: isRejecting }] = useRejectPointMutation();

    const refreshPoints = () => {
        // eslint-disable-next-line no-void
        void refetch();
    };

    const currentPoints = useMemo(() => (data ?? [])
        .filter((point: PointDto) => point.isAccepted)
        .reduce((sum: number, point: PointDto) => sum + point.value, 0), [data]);

    const pendingPoints = useMemo(() => (data ?? [])
        .filter((point: PointDto) => point.isPendingApproval && !point.isAccepted)
        .reduce((sum: number, point: PointDto) => sum + point.value, 0), [data]);

    const handleAccept = async (id: string) => {
        if (!currentEntityId) return;
        await acceptPoint({ entityId: currentEntityId, id }).unwrap();
        refreshPoints();
    };

    const handleReject = async (id: string) => {
        if (!currentEntityId) return;
        await rejectPoint({ entityId: currentEntityId, id }).unwrap();
        refreshPoints();
    };

    const filteredRows = useMemo(() => {
        const term = search.toLowerCase().trim();
        return (data ?? [])
            .filter((p: PointDto) => {
                const matchesSearch = p.description?.toLowerCase().includes(term) ?? true;
                const matchesStatus = (() => {
                    switch (statusFilter) {
                    case 'accepted': return p.isAccepted && !p.isPendingApproval;
                    case 'pending': return p.isPendingApproval && !p.isAccepted;
                    default: return true;
                    }
                })();
                return matchesSearch && matchesStatus;
            })
            .sort((a: PointDto, b: PointDto) => moment(b.timestamp).diff(moment(a.timestamp)));
    }, [data, search, statusFilter]);

    if (isFetching) return <WideSkeleton />;

    const rows = filteredRows.map((row) => (
        <Table.Tr
            key={row.id}
            bg={row.isPendingApproval ? 'var(--mantine-color-yellow-light)' : undefined}
        >
            <Table.Td>
                <span style={{ color: row.value >= 0 ? 'green' : 'red', fontWeight: 600 }}>
                    {row.value > 0 ? `+${row.value}` : row.value}
                </span>
            </Table.Td>
            <Table.Td>{row.description ?? '-'}</Table.Td>
            <Table.Td>{moment(row.timestamp).format('YYYY-MM-DD HH:mm')}</Table.Td>
            <Table.Td>{row.isPendingApproval ? getStatusBadge(row) : '-'}</Table.Td>
            <Table.Td>
                <div className={styles.actions}>
                    {row.isPendingApproval && !row.isAccepted ? (
                        <>
                            <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={CheckmarkFilled}
                                iconDescription={intl.formatMessage(messages.accept)}
                                hasIconOnly
                                disabled={isAccepting || isRejecting}
                                onClick={async () => { await handleAccept(row.id); }}
                                className={styles.acceptButton}
                            />
                            <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={CloseFilled}
                                iconDescription={intl.formatMessage(messages.reject)}
                                hasIconOnly
                                disabled={isAccepting || isRejecting}
                                onClick={async () => { await handleReject(row.id); }}
                                className={styles.rejectButton}
                            />
                        </>
                    ) : (
                        <>
                            <PointOperationRemoval id={row.id} onSuccess={refreshPoints} />
                            <PointsOperationEdit point={row} onSuccess={refreshPoints} />
                        </>
                    )}
                </div>
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <div className={styles.table}>
            <div className={styles.titleContainer}>
                <span className={styles.label}>{intl.formatMessage(messages.title)}</span>
                <AddOrRemovePoints teamMemberId={userId} align="left" onSuccess={refreshPoints} />
            </div>
            <Stack>
                <div className={styles.summary}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>{intl.formatMessage(messages.currentPoints)}</span>
                        <span className={currentPoints >= 0 ? styles.positive : styles.negative}>
                            {getSignedPointsValue(currentPoints)}
                        </span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>{intl.formatMessage(messages.pendingPoints)}</span>
                        <span className={pendingPoints >= 0 ? styles.positive : styles.negative}>
                            {getSignedPointsValue(pendingPoints)}
                        </span>
                    </div>
                </div>
                <div className={styles.filters}>
                    <TextInput
                        hideLabel
                        labelText=""
                        id="search-points-table"
                        placeholder={intl.formatMessage(messages.searchPlaceholder)}
                        value={search}
                        onChange={(e) => { setSearch(e.currentTarget.value); }}
                    />
                    <div className={styles.statusFilters}>
                        {statusFilterOptions.map((option) => (
                            <Button
                                key={option.value}
                                kind={statusFilter === option.value ? 'primary' : 'ghost'}
                                size="sm"
                                onClick={() => { setStatusFilter(option.value); }}
                            >
                                {option.label}
                            </Button>
                        ))}
                    </div>
                </div>
                <Table withRowBorders withColumnBorders striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>{intl.formatMessage(messages.points)}</Table.Th>
                            <Table.Th>{intl.formatMessage(messages.reason)}</Table.Th>
                            <Table.Th>{intl.formatMessage(messages.date)}</Table.Th>
                            <Table.Th>{intl.formatMessage(messages.status)}</Table.Th>
                            <Table.Th>{intl.formatMessage(messages.actions)}</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    {isError
                        ? <ErrorActionState className={styles.error} onClick={refetch} />
                        : <Table.Tbody>{rows}</Table.Tbody>}
                </Table>
            </Stack>
        </div>
    );
};

const PointsHistoryTable = () => {
    const usesPoints = useCurrentEntityUsesPoints();

    if (!usesPoints) {
        return null;
    }

    return <PointsHistoryTableContent />;
};

const messages = defineMessages({
    title: {
        id: 'PointsHistoryTable.title',
        defaultMessage: 'Historia punktów',
    },
    points: {
        id: 'PointsHistoryTable.points',
        defaultMessage: 'Ilość punktów',
    },
    reason: {
        id: 'PointsHistoryTable.reason',
        defaultMessage: 'Powód',
    },
    date: {
        id: 'PointsHistoryTable.date',
        defaultMessage: 'Data',
    },
    status: {
        id: 'PointsHistoryTable.status',
        defaultMessage: 'Status',
    },
    actions: {
        id: 'PointsHistoryTable.actions',
        defaultMessage: 'Operacje',
    },
    searchPlaceholder: {
        id: 'PointsHistoryTable.searchPlaceholder',
        defaultMessage: 'Wyszukaj po opisie',
    },
    currentPoints: {
        id: 'PointsHistoryTable.currentPoints',
        defaultMessage: 'Aktualna liczba punktów',
    },
    pendingPoints: {
        id: 'PointsHistoryTable.pendingPoints',
        defaultMessage: 'Oczekujące punkty',
    },
    accept: {
        id: 'PointsHistoryTable.accept',
        defaultMessage: 'Zaakceptuj',
    },
    reject: {
        id: 'PointsHistoryTable.reject',
        defaultMessage: 'Odrzuć',
    },
});

export default PointsHistoryTable;
